import { Box, Button, Checkbox, Grid, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { ClientAdvisorAtom, ClientAdvisorSelector } from '../../../../State/Advisory/ClientAdvisors.Atom';
import { AdvisorsLinkedToClientsResult } from '../../../../Component/Model/Dto/Advisory/Employee';
import { AdvisoryService } from '../../../../Component/Api/Advisory/AdvisoryService';
import { toast } from 'react-toastify';
import { AdvancedSupplierModuleRole, BEEAdvisoryRole, EEAdvisoryRole, StrategicRelationshipManagerRole } from '../../../../Component/Common/Constants/UserRoles';

export const ClientAdvisors = () => {
    const bee123Employees = useRecoilValue(ClientAdvisorSelector);
    const setBEE123Employees = useSetRecoilState(ClientAdvisorAtom);
    
    const [allBEEAdvisors, setAllBEEAdvisors] = useState<AdvisorsLinkedToClientsResult[]>(bee123Employees?.filter(advisor => advisor.roleName === BEEAdvisoryRole) ?? []);
    const [allEEAdvisors, setAllEEAdvisors] = useState<AdvisorsLinkedToClientsResult[]>(bee123Employees?.filter(advisor => advisor.roleName === EEAdvisoryRole) ?? []);
    const [allAdvancedSupplierManagers, setAllAdvancedSupplierManagers] = useState<AdvisorsLinkedToClientsResult[]>(bee123Employees?.filter(advisor => advisor.roleName === AdvancedSupplierModuleRole) ?? []);
    const [allStrategicRelationshipManagers, setAllStrategicRelationshipManagers] = useState<AdvisorsLinkedToClientsResult[]>(bee123Employees?.filter(advisor => advisor.roleName === StrategicRelationshipManagerRole) ?? []);

    const [checkAllBEEAdvisors, setCheckAllBEEAdvisors] = useState<boolean>(false);
    const [checkAllEEAdvisors, setCheckAllEEAdvisors] = useState<boolean>(false);
    const [checkAllAdvancedSupplierManagers, setCheckAllAdvancedSupplierManagers] = useState<boolean>(false);
    const [checkAllStrategicRelationshipManagers, setCheckAllStrategicRelationshipManagers] = useState<boolean>(false);

    const [checkedBEEAdvisors, setCheckedBEEAdvisors] = useState<AdvisorsLinkedToClientsResult[]>([]);
    const [checkedEEAdvisors, setCheckedEEAdvisors] = useState<AdvisorsLinkedToClientsResult[]>([]);
    const [checkedAdvancedSupplierManagers, setCheckedAdvancedSupplierManagers] = useState<AdvisorsLinkedToClientsResult[]>([]);
    const [checkedStrategicRelationshipManagers, setCheckedStrategicRelationshipManagers] = useState<AdvisorsLinkedToClientsResult[]>([]);

    const [isSaving, setIsSaving] = useState<boolean>(false);

    useEffect(() => {
        const allBEEAdvisorsChecked = allBEEAdvisors.every(advisor => advisor.optIn);
        setCheckAllBEEAdvisors(allBEEAdvisorsChecked);
    }, [allBEEAdvisors]);

    useEffect(() => {
        const allEEAdvisorsChecked = allEEAdvisors.every(advisor => advisor.optIn);
        setCheckAllEEAdvisors(allEEAdvisorsChecked);
    }, [allEEAdvisors]);

    useEffect(() => {
        const allAdvancedSupplierManagersChecked = allAdvancedSupplierManagers.every(asm => asm.optIn);
        setCheckAllAdvancedSupplierManagers(allAdvancedSupplierManagersChecked);
    }, [allAdvancedSupplierManagers]);

    useEffect(() => {
        const allStrategicRelationshipManagersChecked = allStrategicRelationshipManagers.every(srm => srm.optIn);
        setCheckAllStrategicRelationshipManagers(allStrategicRelationshipManagersChecked);
    }, [allStrategicRelationshipManagers]);

    const handleCheckAllBEEAdvisors = (checked: boolean) => {
        setCheckAllBEEAdvisors(checked);
        const updatedAdvisors = allBEEAdvisors.map(advisor => ({
            ...advisor,
            optIn: checked
        }));
        setAllBEEAdvisors(updatedAdvisors);
        setCheckedBEEAdvisors(checked ? updatedAdvisors : []);
    };

    const handleCheckAllEEAdvisors = (checked: boolean) => {
        setCheckAllEEAdvisors(checked);
        const updatedEEAdvisors = allEEAdvisors.map(advisor => ({
            ...advisor,
            optIn: checked
        }));
        setAllEEAdvisors(updatedEEAdvisors);
        setCheckedEEAdvisors(checked ? updatedEEAdvisors : []);
    };

    const handleCheckAllAdvancedSupplierManagers = (checked: boolean) => {        
        setCheckAllAdvancedSupplierManagers(checked);
        const updatedAdvancedSupplierManagers = allAdvancedSupplierManagers.map(asm => ({
            ...asm,
            optIn: checked
        }));
        setAllAdvancedSupplierManagers(updatedAdvancedSupplierManagers);
        setCheckedAdvancedSupplierManagers(checked ? updatedAdvancedSupplierManagers : []);
    };

    const handleCheckAllStrategicRelationshipManagers = (checked: boolean) => {        
        setCheckAllStrategicRelationshipManagers(checked);
        const updatedStrategicRelationshipManagers = allStrategicRelationshipManagers.map(srm => ({
            ...srm,
            optIn: checked
        }));
        setAllStrategicRelationshipManagers(updatedStrategicRelationshipManagers);
        setCheckedStrategicRelationshipManagers(checked ? updatedStrategicRelationshipManagers : []);
    };

    const updateBEEAdvisorState = (employeeId: string, checked: boolean) => {
        const updatedAdvisors = allBEEAdvisors.map(advisor =>
            advisor.employeeId === employeeId ? { ...advisor, optIn: checked } : advisor
        );
        setAllBEEAdvisors(updatedAdvisors);

        if (checked) {
            setCheckedBEEAdvisors([...checkedBEEAdvisors, updatedAdvisors.find(advisor => advisor.employeeId === employeeId)!]);
        } else {
            setCheckedBEEAdvisors(checkedBEEAdvisors.filter(advisor => advisor.employeeId !== employeeId));
        }

        const allBEEAdvisorsChecked = updatedAdvisors.every(advisor => advisor.optIn);
        setCheckAllBEEAdvisors(allBEEAdvisorsChecked);
    };

    const updateEEAdvisorState = (employeeId: string, checked: boolean) => {
        const updatedEEAdvisors = allEEAdvisors.map(advisor =>
            advisor.employeeId === employeeId ? { ...advisor, optIn: checked } : advisor
        );
        setAllEEAdvisors(updatedEEAdvisors);

        if (checked) {
            setCheckedEEAdvisors([...checkedEEAdvisors, updatedEEAdvisors.find(advisor => advisor.employeeId === employeeId)!]);
        } else {
            setCheckedEEAdvisors(checkedEEAdvisors.filter(advisor => advisor.employeeId !== employeeId));
        }

        const allEEAdvisorsChecked = updatedEEAdvisors.every(advisor => advisor.optIn);
        setCheckAllEEAdvisors(allEEAdvisorsChecked);
    };

    const updateAdvancedSupplierManagerState = (employeeId: string, checked: boolean) => {
        const updatedAdvancedSupplierManagers = allAdvancedSupplierManagers.map(asm =>
            asm.employeeId === employeeId ? { ...asm, optIn: checked } : asm
        );

        setAllAdvancedSupplierManagers(updatedAdvancedSupplierManagers);

        if (checked) {
            setCheckedAdvancedSupplierManagers([...checkedAdvancedSupplierManagers, updatedAdvancedSupplierManagers.find(asm => asm.employeeId === employeeId)!]);
        } else {
            setCheckedAdvancedSupplierManagers(checkedAdvancedSupplierManagers.filter(asm => asm.employeeId !== employeeId));
        }

        const allAdvancedSupplierManagersChecked = updatedAdvancedSupplierManagers.every(asm => asm.optIn);
        setCheckAllAdvancedSupplierManagers(allAdvancedSupplierManagersChecked);
    };

    const updateStrategicRelationshipManagerState = (employeeId: string, checked: boolean) => {
        const updatedStrategicRelationshipManagers = allStrategicRelationshipManagers.map(srm =>
            srm.employeeId === employeeId ? { ...srm, optIn: checked } : srm
        );

        setAllStrategicRelationshipManagers(updatedStrategicRelationshipManagers);

        if (checked) {
            setCheckedStrategicRelationshipManagers([...checkedStrategicRelationshipManagers, updatedStrategicRelationshipManagers.find(srm => srm.employeeId === employeeId)!]);
        } else {
            setCheckedStrategicRelationshipManagers(checkedStrategicRelationshipManagers.filter(srm => srm.employeeId !== employeeId));
        }

        const allStrategicRelationshipManagersChecked = updatedStrategicRelationshipManagers.every(srm => srm.optIn);
        setCheckAllStrategicRelationshipManagers(allStrategicRelationshipManagersChecked);
    };

    const saveAdvisors = async () => {
        setIsSaving(true);
        try {
            const allBEE123Employess = [...allBEEAdvisors, ...allEEAdvisors, ...allAdvancedSupplierManagers, ...allStrategicRelationshipManagers];
            const result = await AdvisoryService.updateClientAdvisorLinks(allBEE123Employess);
            setBEE123Employees(result.data)
            toast.success("Advisors have been saved successfully");
        } catch (error: any) {
            toast.error(error)
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    }

    const StyledCheckbox = ({ disabled, checked, onChange }: { disabled?: boolean, checked: boolean; onChange: (event: React.ChangeEvent<HTMLInputElement>) => void }) => {
        return (
            <Checkbox
                sx={{
                    '&.MuiCheckbox-root .MuiSvgIcon-root': {
                        border: '0.0625rem solid #505B60',
                    },
                    '&.Mui-checked .MuiSvgIcon-root': {
                        border: 'transparent',
                        backgroundColor: '#505B60',
                    },
                    '&.Mui-disabled .MuiSvgIcon-root': {
                        backgroundColor: '#d1d1d1',
                    },
                    '&.Mui-disabled .Mui-checked .MuiSvgIcon-root': {
                        color: '#888888',
                    },
                }}
                disabled={disabled}
                checked={checked}
                onChange={onChange}
            />
        );
    };

    return (
        <>
            <Typography variant="h4" color={"primary"}>BEE Advisors</Typography>

            {allBEEAdvisors.length > 0 ? (
                <>
                <Typography variant="body2">
                    <p>The below BEE Advisors are associated to your registration. You may grant or revoke their access to your scorecards.</p>
                </Typography>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <table 
                            key={allBEEAdvisors.length} 
                            style={{ width: '100%' }}>
                            <thead>
                                <tr style={{ background: "var(--semantics-disabled, #E9ECEF)" }}>
                                    <th>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <StyledCheckbox
                                                checked={checkAllBEEAdvisors}
                                                onChange={(e) => handleCheckAllBEEAdvisors(e.target.checked)}
                                            />
                                            <Typography variant='body2' ml={'7px'} fontWeight={'bold'}>Name</Typography>
                                        </Box>
                                    </th>
                                </tr>
                            </thead>
                            <tbody style={{ backgroundColor: 'white' }}>
                                {allBEEAdvisors.map((advisor, index) => (
                                    <tr key={index} style={{ borderBottom: '1px solid #C9C9C980', padding: '10px 0 10px 0' }}>
                                        <td width="150px">
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <StyledCheckbox
                                                    checked={advisor.optIn}
                                                    onChange={(e) => updateBEEAdvisorState(advisor.employeeId, e.target.checked)}
                                                />
                                                <Typography variant='body2' ml={'7px'}>{advisor.advisorName}</Typography>
                                            </Box>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Grid>
                </Grid>
                </>
            ) : (
                <Box style={{ textAlign: 'center' }}>
                    <img
                        src={`${process.env.PUBLIC_URL}/img/verificationProcess/Elements/OhDear.svg`}
                        alt={'Oh Dear'}
                        style={{ maxWidth: '25%', marginTop: '10px' }}>
                    </img>
                    <Box>
                        <Typography variant="body2"> 
                            <p>There are no BEE Advisors associated to your registration</p>
                        </Typography>
                    </Box>
                </Box>
            )}

            <Box mb={2} />

            <Typography variant="h4" color={"primary"}>EE Advisors</Typography>

            {allEEAdvisors.length > 0 ? (
                <>
                <Typography variant="body2">
                    <p>The below EE Advisors are associated to your registration. You may grant or revoke their access to your EE Module data.</p>
                </Typography>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <table
                            key={allEEAdvisors.length}
                            style={{ width: '100%' }}>
                            <thead>
                                <tr style={{ background: "var(--semantics-disabled, #E9ECEF)" }}>
                                    <th>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <StyledCheckbox
                                                checked={checkAllEEAdvisors}
                                                onChange={(e) => handleCheckAllEEAdvisors(e.target.checked)}
                                            />
                                            <Typography variant='body2' ml={'7px'} fontWeight={'bold'}>Name</Typography>
                                        </Box>
                                    </th>
                                </tr>
                            </thead>
                            <tbody style={{ backgroundColor: 'white' }}>
                                {allEEAdvisors.map((advisor, index) => (
                                    <tr key={index} style={{ borderBottom: '1px solid #C9C9C980', padding: '10px 0 10px 0' }}>
                                        <td width="150px">
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <StyledCheckbox
                                                    checked={advisor.optIn}
                                                    onChange={(e) => updateEEAdvisorState(advisor.employeeId, e.target.checked)}
                                                />
                                                <Typography variant='body2' ml={'7px'}>{advisor.advisorName}</Typography>
                                            </Box>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Grid>
                </Grid>
                </>
            ) : (
                <Box style={{ textAlign: 'center' }}>
                    <img
                        src={`${process.env.PUBLIC_URL}/img/verificationProcess/Elements/OhDear.svg`}
                        alt={'Oh Dear'}
                        style={{ maxWidth: '25%', marginTop: '10px' }}>
                    </img>
                    <Box>
                        <Typography variant="body2">
                            <p>There are no EE Advisors associated to your registration</p>
                        </Typography>
                    </Box>
                </Box>
            )}

            <Box mb={2} />

            <Typography variant="h4" color={"primary"}>Advanced Supplier Managers</Typography>

            {allAdvancedSupplierManagers.length > 0 ? (
                <>
                <Typography variant="body2"> 
                    <p>The below Advanced Supplier Managers has read-only access to the Preferential Procurement element on your scorecards. You may grant or revoke their access to your scorecards.</p>
                </Typography>
                
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <table 
                            key={allAdvancedSupplierManagers.length} 
                            style={{ width: '100%' }}>
                            <thead>
                                <tr style={{ background: "var(--semantics-disabled, #E9ECEF)" }}>
                                    <th>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <StyledCheckbox
                                                checked={checkAllAdvancedSupplierManagers}
                                                onChange={(e) => handleCheckAllAdvancedSupplierManagers(e.target.checked)}
                                            />
                                            <Typography variant='body2' ml={'7px'} fontWeight={'bold'}>Name</Typography>
                                        </Box>
                                    </th>
                                </tr>
                            </thead>
                            <tbody style={{ backgroundColor: 'white' }}>
                                {allAdvancedSupplierManagers.map((advisor, index) => (
                                    <tr key={index} style={{ borderBottom: '1px solid #C9C9C980', padding: '10px 0 10px 0' }}>
                                        <td width="150px">
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <StyledCheckbox
                                                    checked={advisor.optIn}
                                                    onChange={(e) => updateAdvancedSupplierManagerState(advisor.employeeId, e.target.checked)}
                                                />
                                                <Typography variant='body2' ml={'7px'}>{advisor.advisorName}</Typography>
                                            </Box>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Grid>
                </Grid>
                </>
            ) : (
                <Box style={{ textAlign: 'center' }}>
                    <img
                        src={`${process.env.PUBLIC_URL}/img/verificationProcess/Elements/OhDear.svg`}
                        alt={'Oh Dear'}
                        style={{ maxWidth: '25%', marginTop: '10px' }}>
                    </img>
                    <Box>
                        <Typography variant="body2"> 
                            <p>There are no Advanced Supplier Managers associated to your registration</p>
                        </Typography>
                    </Box>
                </Box>
            )}
            
            <Box mb={2} />

            <Typography variant="h4" color={"primary"}>Strategic Relationship Managers</Typography>

            {allStrategicRelationshipManagers.length > 0 ? (
                <>
                <Typography variant="body2"> 
                    <p>The below Strategic Relationship Managers are associated to your registration. You may grant or revoke their access to your scorecards.</p>
                </Typography>
                
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <table 
                            key={allStrategicRelationshipManagers.length} 
                            style={{ width: '100%' }}>
                            <thead>
                                <tr style={{ background: "var(--semantics-disabled, #E9ECEF)" }}>
                                    <th>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <StyledCheckbox
                                                checked={checkAllStrategicRelationshipManagers}
                                                onChange={(e) => handleCheckAllStrategicRelationshipManagers(e.target.checked)}
                                            />
                                            <Typography variant='body2' ml={'7px'} fontWeight={'bold'}>Name</Typography>
                                        </Box>
                                    </th>
                                </tr>
                            </thead>
                            <tbody style={{ backgroundColor: 'white' }}>
                                {allStrategicRelationshipManagers.map((srm, index) => (
                                    <tr key={index} style={{ borderBottom: '1px solid #C9C9C980', padding: '10px 0 10px 0' }}>
                                        <td width="150px">
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <StyledCheckbox
                                                    checked={srm.optIn}
                                                    onChange={(e) => updateStrategicRelationshipManagerState(srm.employeeId, e.target.checked)}
                                                />
                                                <Typography variant='body2' ml={'7px'}>{srm.advisorName}</Typography>
                                            </Box>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Grid>
                </Grid>
                </>
            ) : (
                <Box style={{ textAlign: 'center' }}>
                    <img
                        src={`${process.env.PUBLIC_URL}/img/verificationProcess/Elements/OhDear.svg`}
                        alt={'Oh Dear'}
                        style={{ maxWidth: '25%', marginTop: '10px' }}>
                    </img>
                    <Box>
                        <Typography variant="body2"> 
                            <p>There are no Strategic Relationship Managers associated to your registration</p>
                        </Typography>
                    </Box>
                </Box>
            )}


            <Grid item xs={12} mt={1} display='flex'>
                {(allBEEAdvisors.length > 0 || allEEAdvisors.length > 0 || allAdvancedSupplierManagers.length > 0 || allStrategicRelationshipManagers.length > 0) &&
                    <Box mr={1} sx={{ width: 150 }}>
                        <Button variant='contained' color='primary' fullWidth onClick={() => saveAdvisors()} disabled={isSaving}>Save</Button>
                    </Box>
                }
            </Grid>
        </>
    )
}

